// @ts-strict-ignore
import { AppComponentProps } from '../../typescriptHelpers';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { antGold, antGrey } from '../styles/globalStyles';
import { Integration, IntegrationErrorType, SettingType } from '@/types';
import { displayNameForIntegrationType } from '../../helpers/integrationHelpers';
import { tagStyle } from '.././styles/globalStyles';
import IntegrationIcon from '../icons/IntegrationIcon';
import Typography from 'antd/lib/typography';
import Tag from 'antd/lib/tag';
import { displayTextForIntegrationStatus, tagColorForIntegrationStatus } from '../../helpers';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import { Link } from 'react-router-dom';
import { createAppSettingsRouteWithSettingType } from '../../helpers/routeHelpers';
import Button from 'antd/lib/button';
import { ArrowRightOutlined } from '@ant-design/icons';

interface ReleaseStepIntegrationStatusBannerProps extends AppComponentProps {
    integrations: Integration[];
}

export const ReleaseStepIntegrationStatusBanner: React.FC<ReleaseStepIntegrationStatusBannerProps> = (
    props: ReleaseStepIntegrationStatusBannerProps,
) => {
    if (props.integrations.length === 0) {
        return <></>;
    }

    const groupedIntegrationErrors: Integration[][] = (() => {
        const groupsByKey = new Map<string, Integration[]>();

        props.integrations.forEach((integration) => {
            const key = [
                integration.id,
                integration.integrationError?.status,
                integration.integrationError?.message ?? '',
            ].join('::');

            const group = groupsByKey.get(key);
            if (group) {
                group.push(integration);
            } else {
                groupsByKey.set(key, [integration]);
            }
        });

        return Array.from(groupsByKey.values());
    })();

    const getBannerTitle = () => {
        if (groupedIntegrationErrors.length == 1) {
            const integration = groupedIntegrationErrors[0][0];

            if (integration.integrationError.status == IntegrationErrorType.IncompleteData) {
                return (
                    <Text style={{ fontSize: 16, fontWeight: 400, color: antGrey.grey8 }}>
                        Your{' '}
                        <Text style={{ fontSize: 16, fontWeight: 600, color: antGrey.grey8 }}>
                            {displayNameForIntegrationType(integration.id)}{' '}
                        </Text>{' '}
                        integration needs additional setup.{' '}
                    </Text>
                );
            }

            return (
                <Text style={{ fontSize: 16, fontWeight: 400, color: antGrey.grey8 }}>
                    There&apos;s a problem with your{' '}
                    <Text style={{ fontSize: 16, fontWeight: 600, color: antGrey.grey8 }}>
                        {displayNameForIntegrationType(integration.id)}{' '}
                    </Text>{' '}
                    integration.{' '}
                </Text>
            );
        }

        return (
            <Text style={{ fontSize: 16, fontWeight: 400, color: antGrey.grey8 }}>
                There&apos;s a problem with some of your integrations.
            </Text>
        );
    };

    const getBannerDescription = (
        <Text type="secondary" style={{ marginTop: 8 }}>
            You might not be seeing the most up-to-date information on this view.
        </Text>
    );

    const verticalDivider = (
        <Divider
            type="vertical"
            style={{
                height: 20,
                borderWidth: '1px',
                borderColor: antGrey.grey5,
            }}
        />
    );

    const fixTheIssueLink = (integration: Integration) => (
        <Link
            to={{
                pathname: createAppSettingsRouteWithSettingType(props.orgId, props.appId, SettingType.Integrations),
                state: {
                    scrollTo: integration.capabilities[0],
                },
            }}
        >
            <Button type="link" style={{ margin: 0, paddingLeft: 0 }}>
                Fix the issue <ArrowRightOutlined />
            </Button>
        </Link>
    );

    const renderAffectedDestinationsTooltip = (group: Integration[]) => {
        const tooltipContent = (
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {group.map((integration, index) => (
                    <div key={`${integration.installationId}-${index}`}>{integration.alias || integration.name}</div>
                ))}
            </div>
        );

        return (
            <div style={{ display: 'flex' }}>
                <Tooltip title={tooltipContent}>
                    <Text
                        type="secondary"
                        style={{
                            cursor: 'help',
                            textDecorationLine: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: 3,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {group.length} destinations
                    </Text>
                </Tooltip>
                {verticalDivider}
            </div>
        );
    };

    const getBannerIntegrationErrors = () => {
        return groupedIntegrationErrors.map((group, index) => {
            const representativeIntegration = group[0];

            return (
                <div key={index} style={{ marginTop: 16 }}>
                    <Space direction="horizontal">
                        <IntegrationIcon size={25} integrationId={representativeIntegration.id} />
                        <Typography.Text
                            style={{
                                fontSize: 14,
                                fontWeight: 400,
                                color: antGrey.grey8,
                                flex: 1,
                                marginRight: 8,
                            }}
                        >
                            {displayNameForIntegrationType(representativeIntegration.id)}
                        </Typography.Text>
                        <Tag color={tagColorForIntegrationStatus(representativeIntegration)} style={tagStyle}>
                            {displayTextForIntegrationStatus(representativeIntegration)}
                        </Tag>
                        {verticalDivider}
                        {representativeIntegration.integrationError?.message && (
                            <div style={{ display: 'flex' }}>
                                <Tooltip title={representativeIntegration.integrationError.message}>
                                    <Text
                                        type="secondary"
                                        style={{
                                            display: 'block',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '400px',
                                        }}
                                    >
                                        {representativeIntegration.integrationError.message}
                                    </Text>
                                </Tooltip>
                                {verticalDivider}
                            </div>
                        )}
                        {group.length > 1 && renderAffectedDestinationsTooltip(group)}
                        {fixTheIssueLink(representativeIntegration)}
                    </Space>
                </div>
            );
        });
    };

    return (
        <div
            style={{
                backgroundColor: antGold.gold1,
                border: `1px solid ${antGold.gold2}`,
                padding: '24px 36px',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {getBannerTitle()}
            {getBannerDescription}
            {getBannerIntegrationErrors()}
        </div>
    );
};
